import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Badge, BadgeColor} from '@/components/common//badges/Badge'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'

type FormSectionProps = {
  title: string | null
  titleVariant?: TextVariant
  description?: string | ReactNode
  isAnimated?: boolean
  animationStarted?: boolean
  button?: ReactNode
  badge?: {
    text: string
    color?: BadgeColor
  }
  isFullWidth?: boolean
}

export const FormSection: FC<React.PropsWithChildren<FormSectionProps>> = ({
  title,
  titleVariant = TextVariant.h5,
  description,
  isAnimated = false,
  animationStarted = false,
  button,
  badge,
  isFullWidth = true,
  children,
}) => (
  <>
    <div
      className={cn(
        'wrapper',
        isAnimated && 'animated',
        animationStarted && 'animation-started',
        isFullWidth && 'full-width',
      )}
    >
      <div className="header">
        {title && (
          <Text variant={titleVariant} fontWeight={TextFontWeight.bold} element="span">
            <span className="title">{title}</span>
            {badge && (
              <div className="badge">
                <Badge title={badge.text} color={badge.color} />
              </div>
            )}
          </Text>
        )}
        {button}
      </div>
      {description && <div className="description">{description}</div>}
      <div className="content">{children}</div>
    </div>

    <style jsx>{`
      .wrapper {
        width: 100%;
      }
      .wrapper + .wrapper:not(.animated),
      .wrapper + .wrapper.animation-started {
        margin-top: ${theme.spacing[7]};
      }
      .animated {
        opacity: 0;
        visibility: hidden;
        max-height: 0;
        transition: all ${EXPAND_TRANSITION}ms ease-in-out;
      }
      .animation-started {
        opacity: 1;
        visibility: visible;
        max-height: 100%;
      }
      .description {
        margin-top: ${theme.spacing[2]};
      }
      .content {
        margin-top: ${theme.spacing[4]};
      }
      .header {
        display: flex;
        align-items: center;
      }
      .full-width .header {
        justify-content: space-between;
      }
      .title {
        margin-right: ${theme.spacing[2]};
      }
      .badge {
        display: inline-block;
        white-space: nowrap;
        font-weight: ${theme.fontWeight.regular};
      }
    `}</style>
  </>
)
